import type { HelpGuide } from '../types';

export const settingsAuditGuide: HelpGuide = {
  key: 'settings-audit',
  title: 'บันทึกการตรวจสอบ (Audit Log)',
  subtitle: 'เมนู: ระบบ ▸ บันทึกการตรวจสอบ',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/settings-audit/คู่มือการใช้งาน_บันทึกการตรวจสอบ_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “บันทึกการตรวจสอบ (Audit Log)” เป็นทะเบียนบันทึกเหตุการณ์ที่เกี่ยวกับการจัดการผู้ใช้งาน บทบาท (Role) และสิทธิ์การใช้งานของระบบ ทุกครั้งที่มีการสร้างผู้ใช้ใหม่ แก้ไขข้อมูลผู้ใช้ สร้าง/แก้ไขบทบาท กำหนดสิทธิ์ หรือมอบหมายบทบาทให้ผู้ใช้ ระบบจะบันทึกเหตุการณ์ลงในตารางนี้โดยอัตโนมัติ เพื่อใช้ตรวจสอบย้อนหลังว่า “ใคร ทำอะไร กับอะไร เมื่อไร”',
        },
        {
          kind: 'callout',
          text: 'หลักการสำคัญ: บันทึกการตรวจสอบเป็นแบบ “เพิ่มได้อย่างเดียว (Append-only)” — ไม่สามารถแก้ไขหรือลบรายการที่เกิดขึ้นแล้วได้ เพื่อให้เป็นหลักฐานการตรวจสอบที่เชื่อถือได้ (Audit Trail) สำหรับงานควบคุมภายในและการสอบบัญชี',
        },
        { kind: 'para', text: 'จุดเด่นของหน้านี้:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'อ่านอย่างเดียว — ',
              text: 'เป็นหน้าจอดูข้อมูลเท่านั้น ไม่มีปุ่มเพิ่ม/แก้ไข/ลบ และไม่ต้องกดบันทึกใด ๆ',
            },
            {
              lead: 'เรียงล่าสุดก่อน — ',
              text: 'รายการเรียงตามเวลาจากใหม่ไปเก่า เห็นเหตุการณ์ล่าสุดที่ด้านบนสุดเสมอ',
            },
            {
              lead: '5 คอลัมน์ครบถ้วน — ',
              text: 'แต่ละแถวบอก เวลา · ผู้กระทำ · การกระทำ · ประเภท · เป้าหมาย ของแต่ละเหตุการณ์',
            },
            {
              lead: 'ใช้ตรวจสอบย้อนหลัง — ',
              text: 'เหมาะกับงานควบคุมภายใน ตรวจสิทธิ์ผู้ใช้ และตอบข้อสงสัยว่าใครเปลี่ยนการตั้งค่าใด',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/settings-audit/01-overview.png',
          caption: 'ภาพรวมบันทึกการตรวจสอบ แสดงตารางรายการเหตุการณ์เรียงจากใหม่ไปเก่า',
        },
      ],
    },
    {
      id: 'access',
      heading: '2. สิทธิ์การเข้าถึงและการเปิดหน้าจอ',
      blocks: [
        {
          kind: 'para',
          text: 'หน้าบันทึกการตรวจสอบอยู่ภายใต้เมนู ระบบ ▸ บันทึกการตรวจสอบ และจำกัดเฉพาะผู้ที่มีสิทธิ์จัดการผู้ใช้งานเท่านั้น (ผู้ดูแลระบบ/แอดมิน) หากบัญชีของท่านไม่มีสิทธิ์ จะไม่เห็นเมนูนี้หรือไม่สามารถเปิดดูข้อมูลได้',
        },
        {
          kind: 'callout',
          text: 'หน้าจอนี้ไม่มีการมอบหมายให้บันทึกเอง ทุกรายการเกิดขึ้นโดยอัตโนมัติจากการกระทำในหน้า จัดการผู้ใช้งาน และ จัดการบทบาท/สิทธิ์ ของระบบ',
          variant: 'info',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '3. ส่วนประกอบของหน้าจอ',
      blocks: [
        {
          kind: 'para',
          text: 'หน้าจอนี้ประกอบด้วยตารางบันทึกการตรวจสอบเพียงส่วนเดียว แสดงเหตุการณ์ทั้งหมดเรียงจากใหม่ไปเก่า มี 5 คอลัมน์',
        },
        { kind: 'para', text: 'คอลัมน์ในตาราง:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'เวลา — ',
              text: 'วันและเวลาที่เกิดเหตุการณ์ (รูปแบบไทย เช่น 18/6/2569 13:45:30)',
            },
            {
              lead: 'ผู้กระทำ — ',
              text: 'ชื่อ/อีเมลของผู้ใช้ที่เป็นผู้กระทำ ณ เวลาที่เกิดเหตุการณ์',
            },
            {
              lead: 'การกระทำ — ',
              text: 'รหัสการกระทำ (เช่น user.create, role.permissions.set) ดูความหมายได้ที่ตารางด้านล่าง',
            },
            { lead: 'ประเภท — ', text: 'ประเภทของเป้าหมายที่ถูกกระทำ (user / role / userrole)' },
            {
              lead: 'เป้าหมาย — ',
              text: 'รหัสอ้างอิง (UUID) ของเป้าหมายที่ถูกกระทำ หรือ “—” หากเหตุการณ์นั้นไม่มีรหัสเป้าหมายบันทึกไว้',
            },
          ],
        },
      ],
    },
    {
      id: 'table',
      heading: '4. การอ่านตารางบันทึกการตรวจสอบ',
      blocks: [
        {
          kind: 'para',
          text: 'แต่ละแถวในตารางคือเหตุการณ์หนึ่งครั้ง อ่านจากซ้ายไปขวา: เวลาที่เกิด → ใครเป็นผู้กระทำ → ทำการกระทำอะไร → กับเป้าหมายประเภทไหน → และเป้าหมายตัวใด ตัวอย่างเช่น แถวที่มีการกระทำ user.create ประเภท user หมายความว่า “มีการสร้างผู้ใช้ใหม่” โดยผู้กระทำที่ระบุในคอลัมน์ผู้กระทำ',
        },
        {
          kind: 'para',
          text: 'การสร้างผู้ใช้หรือแก้สิทธิ์หนึ่งครั้งอาจทำให้เกิดหลายแถว เช่น สร้างผู้ใช้ใหม่แล้วมอบหมายบทบาท จะเห็นทั้งแถว user.create และแถว userrole.assign ต่อเนื่องกัน ส่วนการกำหนดสิทธิ์ให้บทบาทจะปรากฏเป็นแถว role.permissions.set',
        },
        {
          kind: 'callout',
          text: 'ตารางเรียงรายการจากใหม่ไปเก่าเสมอ เหตุการณ์ล่าสุดจึงอยู่บนสุด เลื่อนตารางลงเพื่อดูเหตุการณ์ย้อนหลัง',
          variant: 'info',
        },
        {
          kind: 'image',
          src: '/help/settings-audit/02-log-table.png',
          caption:
            'ตารางบันทึกการตรวจสอบ แสดง เวลา / ผู้กระทำ / การกระทำ (user.create, role.permissions.set ฯลฯ) / ประเภท / เป้าหมาย',
        },
      ],
    },
    {
      id: 'actions',
      heading: '5. ความหมายของรหัสการกระทำ',
      blocks: [
        {
          kind: 'para',
          text: 'รหัสการกระทำในคอลัมน์ “การกระทำ” เป็นรหัสภาษาอังกฤษที่ระบบบันทึกตามจริง ความหมายมีดังนี้:',
        },
        {
          kind: 'table',
          head: ['การกระทำ', 'ประเภท', 'ความหมาย'],
          rows: [
            ['user.create', 'user', 'สร้างผู้ใช้งานใหม่'],
            ['user.update', 'user', 'แก้ไขข้อมูลผู้ใช้ (เช่น อีเมล หรือรหัส Entra)'],
            ['role.create', 'role', 'สร้างบทบาท (Role) ใหม่'],
            ['role.update', 'role', 'แก้ไขข้อมูลบทบาท (เช่น ชื่อ หรือรหัสบทบาท)'],
            ['role.delete', 'role', 'ลบบทบาท (เกิดไม่บ่อย)'],
            ['role.permissions.set', 'role', 'กำหนด/ปรับสิทธิ์ให้บทบาท'],
            ['userrole.assign', 'userrole', 'มอบหมาย/ปรับบทบาทของผู้ใช้ในบริษัทหนึ่ง'],
          ],
        },
      ],
    },
    {
      id: 'states',
      heading: '6. สถานะการแสดงผลของหน้าจอ',
      blocks: [
        {
          kind: 'para',
          text: 'ขณะเปิดหน้านี้ อาจพบสถานะการแสดงผลแตกต่างกันตามสถานการณ์:',
        },
        {
          kind: 'table',
          head: ['สถานะ', 'สิ่งที่เห็น', 'ความหมาย'],
          rows: [
            ['กำลังโหลด', 'ข้อความ “กำลังโหลด…”', 'ระบบกำลังดึงข้อมูลบันทึก รอสักครู่'],
            ['ไม่มีรายการ', 'ตารางว่าง', 'ยังไม่มีเหตุการณ์ที่ถูกบันทึกในระบบ'],
            ['มีรายการ', 'ตารางแสดงแถวเหตุการณ์', 'มีบันทึกการตรวจสอบให้ดูตามปกติ'],
            [
              'โหลดล้มเหลว',
              'ข้อความ “โหลดล้มเหลว: …” (สีแดง)',
              'ดึงข้อมูลไม่สำเร็จ ลองโหลดหน้าใหม่หรือตรวจสอบสิทธิ์/การเชื่อมต่อ',
            ],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '7. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'แก้ไขหรือลบรายการในบันทึกการตรวจสอบได้ไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่ได้ — เป็นทะเบียนแบบเพิ่มได้อย่างเดียว ทุกรายการเป็นหลักฐานถาวร แก้ไข/ลบไม่ได้',
            },
          ],
        },
        { kind: 'para', text: 'ทำไมการสร้างผู้ใช้ครั้งเดียวจึงมีหลายแถว?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'เพราะการสร้างผู้ใช้พร้อมมอบหมายบทบาทจะถูกบันทึกแยกเหตุการณ์ เช่น user.create และ userrole.assign',
            },
          ],
        },
        { kind: 'para', text: 'คอลัมน์เป้าหมายขึ้นเครื่องหมาย “—” หมายความว่าอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'หมายถึงเหตุการณ์นั้นไม่มีรหัสเป้าหมาย (UUID) บันทึกไว้ ระบบจึงแสดงเป็น “—” แทน',
            },
          ],
        },
        { kind: 'para', text: 'เปิดหน้านี้ไม่ได้ / ไม่เห็นเมนู?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'หน้านี้จำกัดเฉพาะผู้มีสิทธิ์จัดการผู้ใช้งาน (แอดมิน) หากต้องการเข้าถึง โปรดติดต่อผู้ดูแลระบบ',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '8. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Audit Log / บันทึกการตรวจสอบ — ',
              text: 'ทะเบียนเหตุการณ์การเปลี่ยนแปลงผู้ใช้/บทบาท/สิทธิ์ เพื่อตรวจสอบย้อนหลัง',
            },
            {
              lead: 'Append-only — ',
              text: 'เพิ่มได้อย่างเดียว แก้ไขหรือลบรายการเดิมไม่ได้ เพื่อความน่าเชื่อถือของหลักฐาน',
            },
            {
              lead: 'ผู้กระทำ (Actor) — ',
              text: 'ผู้ใช้ที่เป็นผู้ทำเหตุการณ์ ณ เวลานั้น',
            },
            {
              lead: 'การกระทำ (Action) — ',
              text: 'รหัสประเภทเหตุการณ์ที่เกิดขึ้น เช่น user.create, role.permissions.set',
            },
            {
              lead: 'ประเภท (Type) — ',
              text: 'ชนิดของเป้าหมายที่ถูกกระทำ: user (ผู้ใช้) / role (บทบาท) / userrole (บทบาทของผู้ใช้)',
            },
            {
              lead: 'เป้าหมาย (Target) — ',
              text: 'รหัสอ้างอิง (UUID) ของรายการที่ถูกกระทำ',
            },
            {
              lead: 'Role / บทบาท — ',
              text: 'กลุ่มสิทธิ์การใช้งานที่มอบหมายให้ผู้ใช้',
            },
          ],
        },
      ],
    },
  ],
};
